import { EventFormData } from '../types';
import { BalloonEventModel, CategoryModel, apiFetch } from 'ts-core';
import { 
  transformDraftToBalloonEvent, 
  transformBalloonEventToFormData,
  transformFormDataToDraftPayload as transformFormData,
  DraftApiData 
} from '../transformers';
import { storeMediaForDraft, getMediaForDraft, clearMediaForDraft, storeManifestForDraft, getManifestForDraft } from './mediaStore';
import type { ManifestEntry } from './mediaStore';
import { getPendingFile, registerPendingFile } from './imageService';
import { createPreviewUrl } from '../utils/imageUtils';

export type DraftSaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export interface DraftResponse {
  success: boolean;
  message: string;
  draftId?: string;
  userId?: string;
}

export interface DraftListItem {
  id: string;
  name: string;
  location: string;
  dates: string[];
  description: string;
  imageUrl: string | null;
  createdAt: Date;
  updatedAt: Date;
}

export interface DraftListResponse {
  drafts: DraftListItem[];
  total: number;
}

interface DraftApiPayload {
  type: 'balloon';
  draft: {
    name: string;
    categories: Array<{ slug: string }>;
    descriptions: Array<{ language: string; text: string }>;
    dates: Array<{ startDate: string; endDate: string; notes?: string }>;
    location: Array<{
      name: string;
      address: {
        street: string;
        zip: string;
        locality: string;
        country: string;
      };
      coordinates: { lat: number; lng: number };
    }>;
    images: Array<{ url: string; caption?: string }>;
    tags: string[];
    contactInfos: Array<{ type: string; content: string }>;
  };
}

/**
 * Transform EventFormData to API draft payload format
 * Uses the unified transformer from transformers module
 */
function transformFormDataToDraftPayload(formData: EventFormData): DraftApiPayload {
  const result = transformFormData(formData);
  return result as DraftApiPayload;
}

/**
 * Draft Service - handles all draft API operations
 */
export class DraftService {
  private apiEndpoint: string;
  private apiKey: string;

  constructor(apiEndpoint: string, apiKey: string) {
    this.apiEndpoint = apiEndpoint;
    this.apiKey = apiKey;
  }

  /**
   * Create a new draft
   */
  async createDraft(token: string, formData: EventFormData): Promise<DraftResponse> {
    const payload = transformFormDataToDraftPayload(formData);
    
    const response = await apiFetch(`${this.apiEndpoint}/api/v2/users/me/drafts`, {
      method: 'POST',
      apiKey: this.apiKey,
      authToken: token,
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to create draft: ${errorText}`);
    }

    const data = await response.json();
    const draftId = data.data?.id as string | undefined;

    // Persist blob URL files to IndexedDB
    if (draftId) {
      await this.persistMediaFiles(draftId, formData);
    }

    return {
      success: data.success,
      message: data.message,
      draftId,
      userId: data.data?.user_id,
    };
  }

  /**
   * Update an existing draft
   */
  async updateDraft(token: string, draftId: string, formData: EventFormData): Promise<DraftResponse> {
    const payload = transformFormDataToDraftPayload(formData);
    
    const response = await apiFetch(`${this.apiEndpoint}/api/v2/users/me/drafts/${draftId}`, {
      method: 'PUT',
      apiKey: this.apiKey,
      authToken: token,
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to update draft: ${errorText}`);
    }

    const data = await response.json();

    // Persist blob URL files to IndexedDB
    await this.persistMediaFiles(draftId, formData);

    return {
      success: data.success,
      message: data.message,
      draftId: data.data?.id,
      userId: data.data?.user_id,
    };
  }

  /**
   * Get a draft by ID and return raw API data
   * This allows the caller to transform to BalloonEventModel with proper category resolution
   */
  async getDraftRaw(token: string, draftId: string): Promise<{ id: string; draft: DraftApiData } | null> {
    const response = await apiFetch(`${this.apiEndpoint}/api/v2/users/me/drafts/${draftId}`, {
      method: 'GET',
      apiKey: this.apiKey,
      authToken: token,
    });

    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      const errorText = await response.text();
      throw new Error(`Failed to get draft: ${errorText}`);
    }

    const data = await response.json();
    const draft = data.data?.draft;
    
    if (!draft) return null;

    return { id: data.data.id, draft: draft as DraftApiData };
  }

  /**
   * Get a draft by ID as BalloonEventModel
   * This normalizes draft data to the canonical event model
   */
  async getDraftAsBalloonEvent(
    token: string, 
    draftId: string,
    categoryResolver?: (slug: string) => CategoryModel | null
  ): Promise<BalloonEventModel | null> {
    const rawData = await this.getDraftRaw(token, draftId);
    if (!rawData) return null;

    return transformDraftToBalloonEvent(rawData.id, rawData.draft, categoryResolver);
  }

  /**
   * Get a draft by ID as EventFormData (convenience method)
   * Uses BalloonEventModel as intermediate for consistent transformation.
   */
  async getDraft(
    token: string, 
    draftId: string,
    categoryResolver?: (slug: string) => CategoryModel | null
  ): Promise<EventFormData | null> {
    const balloonEvent = await this.getDraftAsBalloonEvent(token, draftId, categoryResolver);
    if (!balloonEvent) return null;

    const formData = transformBalloonEventToFormData(balloonEvent, { isDraft: true, draftId });

    // Restore media files from IndexedDB using manifest (preserves ordering)
    try {
      const manifest = await getManifestForDraft(draftId);
      const storedMedia = await getMediaForDraft(draftId);

      if (manifest && manifest.length > 0) {
        // Manifest-based restore: rebuild images array from manifest
        const mediaByKey = new Map(storedMedia.map(m => [`${draftId}::${m.originalIndex}`, m]));
        const restoredImages: Array<{ url: string; caption?: string; copyright?: string }> = [];

        for (const entry of manifest) {
          if (entry.kind === 'http' && entry.url) {
            restoredImages.push({ url: entry.url, caption: entry.caption, copyright: entry.copyright });
          } else if (entry.kind === 'blob' && entry.blobKey) {
            const media = mediaByKey.get(entry.blobKey);
            if (media) {
              const blobUrl = createPreviewUrl(media.file);
              registerPendingFile(blobUrl, media.file);
              restoredImages.push({ url: blobUrl, caption: entry.caption || media.filename, copyright: entry.copyright });
            }
          }
        }

        formData.images = restoredImages;
        console.log('[DraftService] Restored', restoredImages.length, 'image(s) from manifest');
      } else if (storedMedia.length > 0) {
        // Legacy fallback: no manifest, merge by index (existing behavior)
        const images = formData.images ?? [];
        for (const media of storedMedia) {
          const blobUrl = createPreviewUrl(media.file);
          registerPendingFile(blobUrl, media.file);
          if (media.originalIndex < images.length) {
            images[media.originalIndex] = { url: blobUrl, caption: media.filename };
          } else {
            images.push({ url: blobUrl, caption: media.filename });
          }
        }
        formData.images = images;
        console.log('[DraftService] Restored', storedMedia.length, 'media file(s) from IndexedDB (legacy)');
      }
    } catch (error) {
      console.warn('[DraftService] Failed to restore media from IndexedDB:', error);
    }

    return formData;
  }

  /**
   * Get all drafts for the current user
   */
  async getDrafts(token: string): Promise<DraftListResponse> {
    const response = await apiFetch(`${this.apiEndpoint}/api/v2/users/me/drafts`, {
      method: 'GET',
      apiKey: this.apiKey,
      authToken: token,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch drafts: ${errorText}`);
    }

    const data = await response.json();
    const draftsArray = data.data || [];

    const drafts: DraftListItem[] = draftsArray.map((item: Record<string, unknown>) => {
      const draft = item.draft as Record<string, unknown> | undefined;
      const location = Array.isArray(draft?.location) && draft.location[0]
        ? (draft.location[0] as Record<string, unknown>)
        : null;
      const dates = Array.isArray(draft?.dates)
        ? (draft.dates as Array<{ startDate: string }>).map(d => 
            new Date(d.startDate).toLocaleDateString()
          )
        : [];
      const images = Array.isArray(draft?.images) ? draft.images as Array<{ url: string }> : [];
      const descriptions = Array.isArray(draft?.descriptions)
        ? (draft.descriptions as Array<{ text: string }>)
        : [];

      return {
        id: item.id as string,
        name: (draft?.name as string) || 'Untitled Draft',
        location: (location?.name as string) || '',
        dates,
        description: descriptions[0]?.text || '',
        imageUrl: images[0]?.url || null,
        createdAt: new Date(item.created_at as string),
        updatedAt: new Date(item.updated_at as string),
      };
    });

    return {
      drafts,
      total: drafts.length,
    };
  }

  /**
   * Delete a draft
   */
  async deleteDraft(token: string, draftId: string): Promise<DraftResponse> {
    const response = await apiFetch(`${this.apiEndpoint}/api/v2/users/me/drafts/${draftId}`, {
      method: 'DELETE',
      apiKey: this.apiKey,
      authToken: token,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to delete draft: ${errorText}`);
    }

    const data = await response.json();

    // Clean up IndexedDB media for this draft
    try {
      await clearMediaForDraft(draftId);
    } catch (error) {
      console.warn('[DraftService] Failed to clear media from IndexedDB:', error);
    }

    return {
      success: data.success,
      message: data.message,
    };
  }

  /**
   * Persist all blob URL files to IndexedDB for draft recovery
   */
  private async persistMediaFiles(draftId: string, formData: EventFormData): Promise<void> {
    try {
      const mediaFiles: Array<{ file: File; originalIndex: number }> = [];
      const manifest: ManifestEntry[] = [];
      const images = formData.images ?? [];
      let blobIndex = 0;

      for (let i = 0; i < images.length; i++) {
        const img = images[i];
        if (img.url.startsWith('blob:')) {
          const file = getPendingFile(img.url);
          if (file) {
            const blobKey = `${draftId}::${blobIndex}`;
            mediaFiles.push({ file, originalIndex: blobIndex });
            manifest.push({ kind: 'blob', blobKey, filename: file.name, caption: img.caption, copyright: img.copyright });
            blobIndex++;
          }
        } else if (img.url.startsWith('http://') || img.url.startsWith('https://')) {
          manifest.push({ kind: 'http', url: img.url, caption: img.caption, copyright: img.copyright });
        }
      }

      if (mediaFiles.length > 0) {
        await storeMediaForDraft(draftId, mediaFiles);
        await storeManifestForDraft(draftId, manifest);
        console.log('[DraftService] Persisted', mediaFiles.length, 'blob(s) +', manifest.length, 'manifest entries for draft', draftId);
      } else if (manifest.length > 0) {
        // Only HTTP images — store manifest (orphaned blobs cleaned up on draft delete).
        await storeManifestForDraft(draftId, manifest);
      }
      // NOTE: when mediaFiles AND manifest are both empty, we intentionally leave
      // IndexedDB alone. A transient empty formData.images during a hot-reload or
      // initialization race could otherwise wipe the user's uploaded files.
      // Cleanup happens explicitly on deleteDraft() or clearMediaForDraft().
    } catch (error) {
      console.warn('[DraftService] Failed to persist media to IndexedDB:', error);
    }
  }
}

// Legacy transform function removed - now using unified transformers from ../transformers

// Singleton instance (will be initialized with API config)
let draftServiceInstance: DraftService | null = null;

export function getDraftService(apiEndpoint: string, apiKey: string): DraftService {
  if (!draftServiceInstance || 
      draftServiceInstance['apiEndpoint'] !== apiEndpoint || 
      draftServiceInstance['apiKey'] !== apiKey) {
    draftServiceInstance = new DraftService(apiEndpoint, apiKey);
  }
  return draftServiceInstance;
}
